import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend);

const NonRegisteredUser = () => {
  const [nonRegisteredUsers, setNonRegisteredUsers] = useState([]);
  const [jobSeekers, setJobSeekers] = useState([]);
  const [filters, setFilters] = useState({
    jobType: '', jobDecision: '', firstChoice: '', secondChoice: '', thirdChoice: '',
    position: '', qualification: '', experience: '', expectedSalary: '', jobMode: ''
  });
  const [formData, setFormData] = useState({
    name: '', position: '', qualification: '', email: '', gender: '',
    location: '', phoneNumber: '', jobDecision: '', jobMode: '', jobType: '',
    maritalStatus: '', experience: '', photo: '', expectedSalary: '', firstChoice: '',
    secondChoice: '', thirdChoice: ''
  });
  const [message, setMessage] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [channels, setChannels] = useState([]);
  const [charCount, setCharCount] = useState(0);
  const [chartData, setChartData] = useState(null);
  const [chartType, setChartType] = useState('nonRegistered');

  useEffect(() => {
    fetchNonRegisteredUsers();
    fetchJobSeekers();
    checkDuplicates();
  }, [filters]);

  const fetchNonRegisteredUsers = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}non-registered/all`, { params: filters });
      setNonRegisteredUsers(response.data.data);
    } catch (err) {
      toast.error('Failed to fetch non-registered users');
    }
  };

  const fetchJobSeekers = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}non-registered/jobseeker/allJobSeeker`, { params: filters });
      setJobSeekers(response.data.data);
    } catch (err) {
      toast.error('Failed to fetch job seekers');
    }
  };

  const checkDuplicates = async () => {
    const jobSeekerEmails = (await axios.get(`${process.env.REACT_APP_API_URL}non-registered/jobseeker/allJobSeeker`)).data.data.map(u => u.email);
    const duplicates = nonRegisteredUsers.filter(u => jobSeekerEmails.includes(u.email));
    if (duplicates.length > 0) {
      await axios.post(`${process.env.REACT_APP_API_URL}non-registered/delete-duplicates`, { emails: duplicates.map(u => u.email) });
      fetchNonRegisteredUsers();
      toast.success('Duplicates removed from non-registered users');
    }
  };

  const handleAddNonRegistered = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}non-registered/add`, formData);
      setFormData({
        name: '', position: '', qualification: '', email: '', gender: '',
        location: '', phoneNumber: '', jobDecision: '', jobMode: '', jobType: '',
        maritalStatus: '', experience: '', photo: '', expectedSalary: '', firstChoice: '',
        secondChoice: '', thirdChoice: ''
      });
      fetchNonRegisteredUsers();
      toast.success('Non-registered user added');
    } catch (err) {
      console.error(err);
      toast.error('Failed to add non-registered user');
    }
  };

  const handleSendMessage = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}non-registered/send-message`, {
        users: selectedUsers,
        message,
        channels,
      });
      toast.success('Messages sent successfully');
    } catch (err) {
      toast.error(`Failed to send messages: ${err.response?.data?.message || err.message}`);
    }
  };

  const generateChartData = (data, field) => {
    const counts = data.reduce((acc, user) => {
      acc[user[field] || 'Unknown'] = (acc[user[field] || 'Unknown'] || 0) + 1;
      return acc;
    }, {});
    return {
      labels: Object.keys(counts),
      datasets: [{ data: Object.values(counts), backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'] }],
    };
  };

  const handleFilterChange = (e) => setFilters({ ...filters, [e.target.name]: e.target.value });
  const handleFormChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  return (
    <div className="min-h-screen bg-gray-800 text-white p-6">
      <h1 className="text-3xl font-bold mb-6 text-green-500">Admin Dashboard</h1>

      {/* Add Non-Registered User Form */}
      <div className="mb-8 bg-gray-700 p-6 rounded-lg shadow-lg">
        <h2 className="text-2xl mb-4 text-green-400">Add Non-Registered User</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input required name="name" value={formData.name} onChange={handleFormChange} placeholder="Name" className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          <input required name="position" value={formData.position} onChange={handleFormChange} placeholder="Position" className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          <input required name="qualification" value={formData.qualification} onChange={handleFormChange} placeholder="Qualification" className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          <input required name="email" value={formData.email} onChange={handleFormChange} placeholder="Email" className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          <input required name="gender" value={formData.gender} onChange={handleFormChange} placeholder="Gender" className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          <input name="location" value={formData.location} onChange={handleFormChange} placeholder="Location" className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          <input required name="phoneNumber" value={formData.phoneNumber} onChange={handleFormChange} placeholder="Phone Number" className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          <select name="jobDecision" value={formData.jobDecision} onChange={handleFormChange} className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
            <option value="">Job Decision</option>
            <option value="job-seeker">Job Seeker</option>
            <option value="job-changer">Job Changer</option>
          </select>
          <select name="jobMode" value={formData.jobMode} onChange={handleFormChange} className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
            <option value="">Job Mode</option>
            <option value="artisan">Artisan</option>
            <option value="professional">Professional</option>
            <option value="student">Student</option>
          </select>
          <select name="jobType" value={formData.jobType} onChange={handleFormChange} className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
            <option value="">Job Type</option>
            <option value="remote">Remote</option>
            <option value="hybrid">Hybrid</option>
            <option value="freelance">Freelance</option>
            <option value="contract">Contract</option>
            <option value="fulltime">Fulltime</option>
          </select>
          <input name="maritalStatus" value={formData.maritalStatus} onChange={handleFormChange} placeholder="Marital Status" className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          <input name="experience" value={formData.experience} onChange={handleFormChange} placeholder="Experience (years)" type="number" className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          <input name="photo" value={formData.photo} onChange={handleFormChange} placeholder="Photo URL" className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          <input name="expectedSalary" value={formData.expectedSalary} onChange={handleFormChange} placeholder="Expected Salary" type="number" className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          <input name="firstChoice" value={formData.firstChoice} onChange={handleFormChange} placeholder="First Choice" className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          <input name="secondChoice" value={formData.secondChoice} onChange={handleFormChange} placeholder="Second Choice" className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          <input name="thirdChoice" value={formData.thirdChoice} onChange={handleFormChange} placeholder="Third Choice" className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
        </div>
        <button onClick={handleAddNonRegistered} className="mt-4 px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition duration-300">
          Add User
        </button>
      </div>

      {/* Filters */}
      <div className="mb-8 bg-gray-700 p-6 rounded-lg shadow-lg">
        <h2 className="text-2xl mb-4 text-green-400">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <select name="jobType" value={filters.jobType} onChange={handleFilterChange} className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
            <option value="">Job Type</option>
            <option value="remote">Remote</option>
            <option value="hybrid">Hybrid</option>
            <option value="freelance">Freelance</option>
            <option value="contract">Contract</option>
            <option value="fulltime">Fulltime</option>
          </select>
          <select name="jobDecision" value={filters.jobDecision} onChange={handleFilterChange} className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green500">
            <option value="">Job Decision</option>
            <option value="job-seeker">Job Seeker</option>
            <option value="job-changer">Job Changer</option>
          </select>
          <input name="firstChoice" value={filters.firstChoice} onChange={handleFilterChange} placeholder="First Choice" className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          <input name="secondChoice" value={filters.secondChoice} onChange={handleFilterChange} placeholder="Second Choice" className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          <input name="thirdChoice" value={filters.thirdChoice} onChange={handleFilterChange} placeholder="Third Choice" className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          <input name="position" value={filters.position} onChange={handleFilterChange} placeholder="Position" className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          <input name="qualification" value={filters.qualification} onChange={handleFilterChange} placeholder="Qualification" className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          <input name="experience" value={filters.experience} onChange={handleFilterChange} placeholder="Experience (years)" type="number" className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green500" />
          <input name="expectedSalary" value={filters.expectedSalary} onChange={handleFilterChange} placeholder="Expected Salary" type="number" className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          <select name="jobMode" value={filters.jobMode} onChange={handleFilterChange} className="p-3 border border-gray-600 bg-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
            <option value="">Job Mode</option>
            <option value="artisan">Artisan</option>
            <option value="professional">Professional</option>
            <option value="student">Student</option>
          </select>
        </div>
      </div>

      {/* User Lists */}
      <div className="mb-8 bg-gray-700 p-6 rounded-lg shadow-lg">
        <h2 className="text-2xl mb-4 text-green-400">Non-Registered Users</h2>
        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {nonRegisteredUsers.map(user => (
            <li key={user._id} className="p-4 border border-gray-600 bg-gray-800 rounded-lg flex items-center justify-between">
              <span>{user.name}</span>
              <input type="checkbox" onChange={(e) => setSelectedUsers(e.target.checked ? [...selectedUsers, user] : selectedUsers.filter(u => u._id !== user._id))} className="ml-2" />
            </li>
          ))}
        </ul>
        <h2 className="text-2xl mt-6 mb-4 text-green-400">Job Seekers</h2>
        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {jobSeekers.map(user => (
            <li key={user._id} className="p-4 border border-gray-600 bg-gray-800 rounded-lg flex items-center justify-between">
              <span>{user.firstName} {user.lastName}</span>
              <input type="checkbox" onChange={(e) => setSelectedUsers(e.target.checked ? [...selectedUsers, user] : selectedUsers.filter(u => u._id !== user._id))} className="ml-2" />
            </li>
          ))}
        </ul>
      </div>

      {/* Message Section */}
      <div className="mb-8 bg-gray-700 p-6 rounded-lg shadow-lg">
        <h2 className="text-2xl mb-4 text-green-400">Send Message</h2>
        <textarea value={message} onChange={(e) => { setMessage(e.target.value); setCharCount(e.target.value.length); }} className="p-3 border border-gray-600 bg-gray-800 rounded-lg w-full h-32 focus:outline-none focus:ring-2 focus:ring-green-500" />
        <p className="mt-2 text-gray-300">Characters: {charCount}</p>
        <div className="mt-4 flex space-x-4">
          <label className="flex items-center">
            <input type="checkbox" value="email" onChange={(e) => setChannels(e.target.checked ? [...channels, e.target.value] : channels.filter(c => c !== e.target.value))} className="mr-2" /> Email
          </label>
          <label className="flex items-center">
            <input type="checkbox" value="sms" onChange={(e) => setChannels(e.target.checked ? [...channels, e.target.value] : channels.filter(c => c !== e.target.value))} className="mr-2" /> SMS
          </label>
          <label className="flex items-center">
            <input type="checkbox" value="whatsapp" onChange={(e) => setChannels(e.target.checked ? [...channels, e.target.value] : channels.filter(c => c !== e.target.value))} className="mr-2" /> WhatsApp
          </label>
        </div>
        <button onClick={handleSendMessage} className="mt-4 px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition duration-300">
          Send
        </button>
      </div>

      {/* Pie Charts */}
      <div className="bg-gray-700 p-6 rounded-lg shadow-lg">
        <h2 className="text-2xl mb-4 text-green-400">Charts</h2>
        <select value={chartType} onChange={(e) => setChartType(e.target.value)} className="p-3 border border-gray-600 bg-gray-800 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-green-500">
          <option value="nonRegistered">Non-Registered Users</option>
          <option value="jobSeekers">Job Seekers</option>
        </select>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {['jobType', 'jobDecision', 'jobMode'].map(field => (
            <div key={field} className="bg-gray-800 p-4 rounded-lg">
              <h3 className="text-lg mb-2 text-green-400">{field}</h3>
              <Pie data={generateChartData(chartType === 'nonRegistered' ? nonRegisteredUsers : jobSeekers, field)} className="w-full" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NonRegisteredUser;